import { Component, ElementRef, input, model, viewChild, output, inject } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl, FormsModule } from '@angular/forms';
import { noop } from 'rxjs';

import { ApButtonComponent } from '@ap/button';

import { ApFileValueAccessor } from '../file.directive';

import { ReadFile } from './../read-file.model';
import { ApFileReaderService } from './../fileReader.service';

@Component({
    selector: 'ap-file-upload-button',
    templateUrl: './upload-button.component.html',
    styleUrls: ['./upload-button.component.scss'],
    imports: [ApButtonComponent, ApFileValueAccessor, FormsModule],
    providers: [ApFileReaderService]
})
export class ApUploadButtonComponent implements ControlValueAccessor {
    protected control = inject(NgControl, { self: true, optional: true });
    protected fileReaderService = inject(ApFileReaderService);

    /**
     * Required - The button label
     */
    public readonly label = input.required<string>();

    /**
     * Acceptable mime types and / or extensions, will be directly fed to the input element
     */
    public readonly accept = input<string>(undefined);

    /**
     * Flag which indicates if the button should be disabled. Default is `false`
     */
    public readonly isDisabled = model(false);

    /**
     * Will show a compact version of a button. Default is `false`
     */
    public readonly compact = input(false);

    /**
     * Will allow input to select multiple. Default is `false`
     */
    public readonly allowMultiple = input(false);

    /**
     * Will show a wide version of a button. Default is `false`
     */
    public readonly wide = input(false);

    /**
     * Color scheme of the button, available primary, secondary (defaults to secondary)
     */
    public readonly variant = input<'primary' | 'secondary'>('secondary');

    /**
     * Event emitter for when a file is 'changed'
     * @internal
     */
    protected readonly fileChanged = output<Array<ReadFile>>();

    /**
     *
     * @protected
     */
    protected readonly input = viewChild<ElementRef>('fileInput');

    /**
     * An array of acceptedFileExtensions
     */
    public readonly acceptedFileExtensions = input<string[]>(undefined);

    protected readonly fileFormControl = input.required<FormControl<ReadFile[]>>();

    public files: FileList;

    protected onChange: (value: any) => void = () => {};
    protected onTouched: () => void = noop;

    constructor() {
        this.control && (this.control.valueAccessor = this);
    }

    protected selectFiles() {
        this.input().nativeElement.click();
    }

    protected onFileSelected(files: any) {
        this.files = files.target.files;

        const fileList = Array.from(files.target.files) as File[];
        this.fileReaderService.readFilesAsArray(fileList).subscribe((results) => this.fileChanged.emit(results));
    }

    public onInputChange(event: Event) {
        this.files = (event.target as HTMLInputElement).files;
        this.onChange(this.files);
        this.onFileSelected(event);
        this.onTouched();

        //reset the input so on delete same files can be selected
        this.input().nativeElement.value = '';
    }

    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    public writeValue(files: any): void {
        this.files = files;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }
}
